import { useTranslation } from 'next-i18next/pages';
import { useRouter } from 'next/router';
import { useMemo } from 'react';

import { JobTitle } from '@pflegia/data-access';
import { JOB_TITLES_TRANSLATIONS, JobTitleEnum, PRAXIA_SEO_JOB_TITLES } from '@pflegia/types/api/job-title/job-titles.enum';
import { SourceDomain } from '@pflegia/types/api/source-domain/source-domain';
import { useCanonicalUrl } from '@pflegia/ui/hooks/use-canonical-url';
import { useFlavorFromRoute } from '@pflegia/ui/hooks/use-flavor-from-route';
import { JOB_BOARD, JOBS } from '@pflegia/ui/pflegia/constants/routes';
import { stripFlavor } from '@pflegia/util/common/path-utils';
import { removeTrailingSlash } from '@pflegia/util/common/remove-trailing-slash';
import { isDefined } from '@pflegia/util/common/type-guards';

import getCanonicalForTransparentPages from '@domains/job-board/utils/get-canonical-for-transparent-pages';
import { parseJobsPageFromSlugs } from '@domains/job-board/utils/parse-jobs-page-from-slugs';

import { NOT_FOUND_ROUTE_QUERY } from '@shared/constants/job-offer';
import { useRouterHelper } from '@shared/hooks/use-router-helper';

interface SeoObjectsProps {
  jobTitle?: JobTitle | null;
  location?: string;
  canBrowseWithoutFilters?: boolean;
  jobTitleForSeo?: string;
  path?: string;
  seoOverrides?: { title?: string | null; description?: string | null } | null;
}

const getJobsSlugCount = (path: string): number => {
  const [strippedPath] = stripFlavor(path).split(/[?#]/);
  const [routePrefix, ...slugSegments] = strippedPath.split('/').filter(Boolean);

  if (routePrefix !== JOB_BOARD.slice(1) && routePrefix !== JOBS.slice(1)) {
    return 0;
  }

  return slugSegments.length;
};

const useJobBoardSeoObjects = ({ jobTitle, location, canBrowseWithoutFilters, jobTitleForSeo, path, seoOverrides }: SeoObjectsProps) => {
  const { t } = useTranslation('jobBoard', { keyPrefix: 'seo' });
  const { sourceDomainName: domain, sourceDomain } = useFlavorFromRoute();

  const { isTransparentJobBoard, isAnyJobPage } = useRouterHelper();
  const router = useRouter();
  const { source } = router.query;
  const isJobOfferNotFound = source === NOT_FOUND_ROUTE_QUERY;
  const resolvedSeoPath = path ?? router.asPath;
  const { page: currentPage } = parseJobsPageFromSlugs(router.query.slugs as string | string[] | undefined);
  const resolvedSeoPathname = new URL(resolvedSeoPath, process.env.NEXT_PUBLIC_BASE_URL).pathname;
  const pathnameWithoutPage = removeTrailingSlash(resolvedSeoPathname).replace(/\/\d+$/, '');
  const pagedSeoPath = `${pathnameWithoutPage}${currentPage > 1 ? `/${currentPage}` : ''}`;

  const shouldRenderSeoWithFilters = useMemo(() => {
    const realFilterSlugCount = getJobsSlugCount(pathnameWithoutPage);
    // Both filtered templates interpolate JOB_TITLE and LOCATION, so without either value they
    // would render a title with a blank placeholder instead of the generic no-filter copy.
    const hasSeoFilterValues = Boolean(jobTitleForSeo) || Boolean(location);
    return (Boolean(jobTitleForSeo) && realFilterSlugCount >= 2) || (Boolean(canBrowseWithoutFilters) && hasSeoFilterValues);
  }, [jobTitleForSeo, location, pathnameWithoutPage, canBrowseWithoutFilters]);

  // When no job position is selected, always use Pflegia as canonical (Pflegia has more content and is more important)
  const sourceDomainFromJobTitle = jobTitle ? getFlavorDomainFromJobTitle(jobTitle) : SourceDomain.PflegiaDe;

  const canonicalUrl = useCanonicalUrl();

  const shouldUseSelfReferencingCanonical = isAnyJobPage;

  const canonical = canonicalUrl({
    defaultDomain: sourceDomain,
    flavorDomain: sourceDomainFromJobTitle,
    path: shouldUseSelfReferencingCanonical ? pagedSeoPath : getCanonicalForTransparentPages(pagedSeoPath, JOBS),
    removeQueryParams: true,
  });

  const seoObjects = useMemo(() => {
    const shouldAllowFollow = isAnyJobPage && !isJobOfferNotFound;
    const robotsProps =
      isTransparentJobBoard && !isJobOfferNotFound ? { noindex: false, nofollow: false } : { noindex: true, nofollow: !shouldAllowFollow };

    const shouldFormatTitleWithFilters =
      isDefined(jobTitleForSeo) &&
      [JOB_TITLES_TRANSLATIONS[JobTitleEnum.Nurse], JOB_TITLES_TRANSLATIONS[JobTitleEnum.AssistantNurse]].includes(jobTitleForSeo);

    if (shouldRenderSeoWithFilters) {
      return {
        title:
          seoOverrides?.title ||
          t(shouldFormatTitleWithFilters ? 'formattedTitleWithFilters' : 'titleWithFilters', {
            JOB_TITLE: formatJobTitleForSeoTitle(jobTitleForSeo, shouldFormatTitleWithFilters),
            LOCATION: location || t('locationFallback'),
          }),
        description:
          seoOverrides?.description ||
          t('descriptionWithFilters', {
            DOMAIN: domain,
            JOB_TITLE: jobTitleForSeo,
            LOCATION: location || t('locationFallback'),
          }),
        canonical,
        ...robotsProps,
      };
    }

    return {
      title: seoOverrides?.title || t('titleNoneFilters'),
      description: seoOverrides?.description || t('descriptionNoneFilters'),
      canonical,
      ...robotsProps,
    };
  }, [
    isAnyJobPage,
    isTransparentJobBoard,
    isJobOfferNotFound,
    shouldRenderSeoWithFilters,
    t,
    canonical,
    jobTitleForSeo,
    location,
    domain,
    seoOverrides?.title,
    seoOverrides?.description,
  ]);

  return { seoObjects, shouldRenderSeoWithFilters };
};

const getFlavorDomainFromJobTitle = (jobTitle?: JobTitle) =>
  PRAXIA_SEO_JOB_TITLES.includes(jobTitle?.title as JobTitleEnum)
    ? // Only praxia job-titles have a praxia flavor
      SourceDomain.PraxiaDe
    : SourceDomain.PflegiaDe;

/**
 * Formats a job title string for use in the SEO <title> tag:
 * - Replaces hyphen-space (e.g. "Gesundheits- und") with em-dash + spaces ("Gesundheits — und")
 * - Removes the German gendered suffix "/in" (e.g. "Pfleger/in" → "Pfleger")
 */
const formatJobTitleForSeoTitle = (jobTitle?: string, shouldFormatWithFilters = false): string | undefined => {
  if (!jobTitle) return jobTitle;
  return shouldFormatWithFilters
    ? jobTitle.replaceAll('-', '').replaceAll(/\/in\b/g, '')
    : jobTitle.replaceAll('- ', ' — ').replaceAll(/\/in\b/g, '');
};

export default useJobBoardSeoObjects;
